import { useState, type FormEvent } from "react";

import { Wordmark } from "../../components/Wordmark";
import { t } from "../../i18n";
import { authLink } from "../../lib/authLink";
import { supabase } from "../../lib/supabase";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // An expired or already-used invite link lands here carrying Supabase's reason.
  const [error, setError] = useState(authLink.errorDescription ?? "");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    // supabase-js reports a refusal as `error`, but a network failure can still
    // throw; either way the message shows and the button comes back.
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInError) setError(signInError.message);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function sendReset() {
    const address = email.trim();
    setError("");

    // The email field doubles as the reset form, so there is nothing to send
    // until it is filled in.
    if (!address) {
      setError(
        t("Enter your email address first, then choose Forgot password."),
      );
      return;
    }

    setBusy(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        address,
        {
          redirectTo: window.location.origin,
        },
      );
      if (resetError) {
        setError(resetError.message);
        return;
      }
      setSent(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="login" id="loginView">
        <div className="card">
          <Wordmark />
          <h1>{t("Check your email")}</h1>
          {/* Phrased so it does not reveal whether the address has an account. */}
          <p>
            {t(
              "If an account exists for {email}, a link to set a new password is on its way. It expires in an hour.",
              { email: email.trim() },
            )}
          </p>
          <button type="button" onClick={() => setSent(false)}>
            {t("Back to sign in")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login" id="loginView">
      <form
        className="card"
        // Fire and forget: submit() shows its own errors.
        onSubmit={(event) => void submit(event)}
      >
        <Wordmark />
        <input
          type="email"
          placeholder={t("Email")}
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="password"
          placeholder={t("Password")}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" disabled={busy}>
          {busy ? t("Signing in…") : t("Sign in")}
        </button>
        <button
          type="button"
          className="linkbtn"
          // Fire and forget: sendReset() shows its own errors.
          onClick={() => void sendReset()}
          disabled={busy}
        >
          {t("Forgot password?")}
        </button>
        {error && <div className="err">{error}</div>}
      </form>
    </div>
  );
}
